/**
 * Per-page Provider for the Adesa Run List workflow.
 * Exposes session, file actions, build-run, run-detail (current working
 * sheet), edit-vehicle, complete-run, and runs history. The page wraps
 * itself in <WholesaleAuctionProvider> — sibling to <WholesaleProvider>
 * (Cleared List workflow), independent state.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react"

import {
  AuctionFileSummary,
  AuctionRunDetail,
  AuctionRunSummary,
  AuctionSession,
  AuctionVehicle,
  AuctionVehicleEditRequest,
  AuctionVendor,
  buildAuctionRun as apiBuildAuctionRun,
  completeAuctionRun as apiCompleteAuctionRun,
  reopenAuctionRun as apiReopenAuctionRun,
  deleteAuctionFile as apiDeleteAuctionFile,
  clearAuctionSession as apiClearAuctionSession,
  editAuctionVehicle as apiEditAuctionVehicle,
  getAuctionRun as apiGetAuctionRun,
  getAuctionSession as apiGetAuctionSession,
  listAuctionRuns as apiListAuctionRuns,
  selectAuctionCandidate as apiSelectAuctionCandidate,
  uploadAuctionFile as apiUploadAuctionFile,
} from "../api/wholesaleAuction"
import { useDealerGroup } from "./DealerGroupContext"

interface WholesaleAuctionContextValue {
  // Session (current uploaded files)
  session: AuctionSession | null
  sessionLoading: boolean
  sessionError: string | null
  refreshSession: () => Promise<void>

  // File actions
  uploadFile: (file: File, overrideVendor?: AuctionVendor) => Promise<AuctionFileSummary>
  deleteFile: (fileId: number) => Promise<void>
  clearSession: () => Promise<void>
  fileActionLoading: boolean
  fileActionError: string | null

  // Build run from uploaded files
  buildRun: () => Promise<AuctionRunSummary>
  buildLoading: boolean
  buildError: string | null

  // Current run detail (the working sheet)
  runDetail: AuctionRunDetail | null
  loadRunDetail: (runId: number) => Promise<void>
  resumeLatestDraft: () => Promise<void>
  clearRunDetail: () => void
  runDetailLoading: boolean
  runDetailError: string | null

  // Per-vehicle edits
  editVehicle: (
    vehicleId: number,
    updates: AuctionVehicleEditRequest,
  ) => Promise<AuctionVehicle>
  editError: string | null

  // Resolve an ambiguous VAUTO match by picking which candidate to use. Recon
  // notes follow the pick automatically.
  selectCandidate: (vehicleId: number, candidateStock: string) => Promise<AuctionVehicle>
  selectError: string | null

  // Complete (lock) the current run
  completeRun: (runId: number) => Promise<AuctionRunSummary>
  completeLoading: boolean
  completeError: string | null
  // Reopen (unlock) a completed run back to draft
  reopenRun: (runId: number) => Promise<AuctionRunSummary>
  reopenLoading: boolean
  reopenError: string | null

  // Runs list (Reports page)
  runs: AuctionRunSummary[]
  runsLoading: boolean
  runsError: string | null
  refreshRuns: () => Promise<void>
}

const WholesaleAuctionContext = createContext<WholesaleAuctionContextValue | undefined>(
  undefined,
)

export const WholesaleAuctionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Working surface: runs are scoped to the active group; a concrete group is
  // required to build a run (the page shows a notice under "All Groups").
  const { dealerGroupId } = useDealerGroup()

  // ---- Session ----
  const [session, setSession] = useState<AuctionSession | null>(null)
  const [sessionLoading, setSessionLoading] = useState(true)
  const [sessionError, setSessionError] = useState<string | null>(null)

  const refreshSession = useCallback(async () => {
    setSessionLoading(true)
    setSessionError(null)
    try {
      const next = await apiGetAuctionSession()
      setSession(next)
    } catch (err) {
      setSessionError(err instanceof Error ? err.message : "Failed to load session")
    } finally {
      setSessionLoading(false)
    }
  }, [])

  // ---- File actions ----
  const [fileActionLoading, setFileActionLoading] = useState(false)
  const [fileActionError, setFileActionError] = useState<string | null>(null)

  const uploadFile = useCallback(
    async (file: File, overrideVendor?: AuctionVendor): Promise<AuctionFileSummary> => {
      setFileActionLoading(true)
      setFileActionError(null)
      try {
        const summary = await apiUploadAuctionFile(file, overrideVendor)
        await refreshSession()
        return summary
      } catch (err) {
        const message = err instanceof Error ? err.message : "Upload failed"
        setFileActionError(message)
        throw err
      } finally {
        setFileActionLoading(false)
      }
    },
    [refreshSession],
  )

  const deleteFile = useCallback(
    async (fileId: number): Promise<void> => {
      setFileActionLoading(true)
      setFileActionError(null)
      try {
        await apiDeleteAuctionFile(fileId)
        await refreshSession()
      } catch (err) {
        const message = err instanceof Error ? err.message : "Delete failed"
        setFileActionError(message)
        throw err
      } finally {
        setFileActionLoading(false)
      }
    },
    [refreshSession],
  )

  // Clear every uploaded file at once — "Start New Run" resets the workspace
  // so the next auction starts from empty upload slots.
  const clearSession = useCallback(async (): Promise<void> => {
    setFileActionLoading(true)
    setFileActionError(null)
    try {
      await apiClearAuctionSession()
      await refreshSession()
    } catch (err) {
      const message = err instanceof Error ? err.message : "Clear failed"
      setFileActionError(message)
      throw err
    } finally {
      setFileActionLoading(false)
    }
  }, [refreshSession])

  // ---- Build run ----
  const [buildLoading, setBuildLoading] = useState(false)
  const [buildError, setBuildError] = useState<string | null>(null)

  const buildRun = useCallback(async (): Promise<AuctionRunSummary> => {
    setBuildLoading(true)
    setBuildError(null)
    try {
      const run = await apiBuildAuctionRun(dealerGroupId)
      return run
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to build run"
      setBuildError(message)
      throw err
    } finally {
      setBuildLoading(false)
    }
  }, [dealerGroupId])

  // ---- Run detail (the working sheet) ----
  const [runDetail, setRunDetail] = useState<AuctionRunDetail | null>(null)
  const [runDetailLoading, setRunDetailLoading] = useState(false)
  const [runDetailError, setRunDetailError] = useState<string | null>(null)

  const loadRunDetail = useCallback(async (runId: number) => {
    setRunDetailLoading(true)
    setRunDetailError(null)
    try {
      const detail = await apiGetAuctionRun(runId)
      setRunDetail(detail)
    } catch (err) {
      setRunDetailError(err instanceof Error ? err.message : "Failed to load run")
    } finally {
      setRunDetailLoading(false)
    }
  }, [])

  // Resume an in-progress (draft) run on a fresh page visit so the working
  // sheet reappears days after Build Run without detouring through Reports.
  // Loads the most recent draft only — a completed run is done, so the page
  // falls back to the upload / Build Run state for the next auction.
  const resumeLatestDraft = useCallback(async () => {
    if (dealerGroupId === null) return
    try {
      const res = await apiListAuctionRuns(50, dealerGroupId)
      const latestDraft = res.runs.find((r) => r.status === "draft")
      if (latestDraft) await loadRunDetail(latestDraft.id)
    } catch {
      // Non-fatal — the page falls back to the upload / Build Run state.
    }
  }, [loadRunDetail, dealerGroupId])

  // Drops the current run from local state so the page falls back to
  // upload chrome. Used by "Start New Run" after completing — doesn't
  // touch the DB or the upload slots, just clears the loaded detail.
  const clearRunDetail = useCallback(() => {
    setRunDetail(null)
    setRunDetailError(null)
  }, [])

  // ---- Per-vehicle edits ----
  // Optimistic + rollback behavior lives in the table component (step 11) —
  // this layer just routes the PATCH and splices the updated row back into
  // runDetail so subsequent renders reflect server-side computed values.
  const [editError, setEditError] = useState<string | null>(null)

  const editVehicle = useCallback(
    async (vehicleId: number, updates: AuctionVehicleEditRequest): Promise<AuctionVehicle> => {
      setEditError(null)
      try {
        const updated = await apiEditAuctionVehicle(vehicleId, updates)
        setRunDetail((current) => {
          if (!current) return current
          return {
            ...current,
            vehicles: current.vehicles.map((v) => (v.id === updated.id ? updated : v)),
          }
        })
        return updated
      } catch (err) {
        const message = err instanceof Error ? err.message : "Edit failed"
        setEditError(message)
        throw err
      }
    },
    [],
  )

  // ---- Candidate selection (resolve ambiguous match) ----
  const [selectError, setSelectError] = useState<string | null>(null)

  const selectCandidate = useCallback(
    async (vehicleId: number, candidateStock: string): Promise<AuctionVehicle> => {
      setSelectError(null)
      try {
        const updated = await apiSelectAuctionCandidate(vehicleId, candidateStock)
        setRunDetail((current) => {
          if (!current) return current
          return {
            ...current,
            vehicles: current.vehicles.map((v) => (v.id === updated.id ? updated : v)),
          }
        })
        return updated
      } catch (err) {
        const message = err instanceof Error ? err.message : "Selection failed"
        setSelectError(message)
        throw err
      }
    },
    [],
  )

  // ---- Complete run ----
  const [completeLoading, setCompleteLoading] = useState(false)
  const [completeError, setCompleteError] = useState<string | null>(null)

  const completeRun = useCallback(async (runId: number): Promise<AuctionRunSummary> => {
    setCompleteLoading(true)
    setCompleteError(null)
    try {
      const run = await apiCompleteAuctionRun(runId)
      setRunDetail((current) => {
        if (!current || current.run.id !== run.id) return current
        return { ...current, run }
      })
      return run
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to complete run"
      setCompleteError(message)
      throw err
    } finally {
      setCompleteLoading(false)
    }
  }, [])

  // ---- Reopen run ----
  const [reopenLoading, setReopenLoading] = useState(false)
  const [reopenError, setReopenError] = useState<string | null>(null)

  const reopenRun = useCallback(async (runId: number): Promise<AuctionRunSummary> => {
    setReopenLoading(true)
    setReopenError(null)
    try {
      const run = await apiReopenAuctionRun(runId)
      setRunDetail((current) => {
        if (!current || current.run.id !== run.id) return current
        return { ...current, run }
      })
      return run
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to reopen run"
      setReopenError(message)
      throw err
    } finally {
      setReopenLoading(false)
    }
  }, [])

  // ---- Runs list (Reports page) ----
  const [runs, setRuns] = useState<AuctionRunSummary[]>([])
  const [runsLoading, setRunsLoading] = useState(false)
  const [runsError, setRunsError] = useState<string | null>(null)

  const refreshRuns = useCallback(async () => {
    if (dealerGroupId === null) {
      setRuns([])
      setRunsLoading(false)
      return
    }
    setRunsLoading(true)
    setRunsError(null)
    try {
      const response = await apiListAuctionRuns(50, dealerGroupId)
      setRuns(response.runs)
    } catch (err) {
      setRunsError(err instanceof Error ? err.message : "Failed to load runs")
    } finally {
      setRunsLoading(false)
    }
  }, [dealerGroupId])

  const value = useMemo<WholesaleAuctionContextValue>(
    () => ({
      session,
      sessionLoading,
      sessionError,
      refreshSession,
      uploadFile,
      deleteFile,
      clearSession,
      fileActionLoading,
      fileActionError,
      buildRun,
      buildLoading,
      buildError,
      runDetail,
      loadRunDetail,
      resumeLatestDraft,
      clearRunDetail,
      runDetailLoading,
      runDetailError,
      editVehicle,
      editError,
      selectCandidate,
      selectError,
      completeRun,
      completeLoading,
      completeError,
      reopenRun,
      reopenLoading,
      reopenError,
      runs,
      runsLoading,
      runsError,
      refreshRuns,
    }),
    [
      session, sessionLoading, sessionError, refreshSession,
      uploadFile, deleteFile, clearSession, fileActionLoading, fileActionError,
      buildRun, buildLoading, buildError,
      runDetail, loadRunDetail, resumeLatestDraft, clearRunDetail, runDetailLoading, runDetailError,
      editVehicle, editError,
      selectCandidate, selectError,
      completeRun, completeLoading, completeError,
      reopenRun, reopenLoading, reopenError,
      runs, runsLoading, runsError, refreshRuns,
    ],
  )

  return (
    <WholesaleAuctionContext.Provider value={value}>
      {children}
    </WholesaleAuctionContext.Provider>
  )
}

export const useWholesaleAuction = (): WholesaleAuctionContextValue => {
  const ctx = useContext(WholesaleAuctionContext)
  if (!ctx) {
    throw new Error("useWholesaleAuction must be used within WholesaleAuctionProvider")
  }
  return ctx
}
